import { useEffect, useState } from 'react';
import { useParams, useNavigate } from '@tanstack/react-router';
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  Loader2,
  MapPin,
  Pencil,
} from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader, type BreadcrumbItem } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Stack } from '@/components/ui/stack';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { FieldDetail } from '@/components/fields/FieldOverviewTab';
import { FieldTerrainTab } from '@/components/fields/FieldTerrainTab';
import { FieldInspectionsTab } from '@/components/fields/inspections/FieldInspectionsTab';
// Карточка поля v3 (эталон founder 2026-07-11, + AK-736 «Тех. операции» 21.08):
// 7 вкладок — Обзор · Севооборот · Геометрия · Журнал · Тех.операции ·
// Кадастры · Управление.
import {
  FieldPassportTab,
  type FieldSecondaryView,
} from '@/components/fields/card/FieldPassportTab';
import { OccupySeasonTab } from '@/components/fields/card/OccupySeasonTab';
import { RotationHistoryTab } from '@/components/fields/card/history/RotationHistoryTab';
import { FieldManagementTab } from '@/components/fields/card/FieldManagementTab';
import { FieldJournalTab } from '@/components/fields/card/FieldJournalTab';
import { FieldOperationsTab } from '@/components/fields/card/FieldOperationsTab';
import { FieldGeometryTab } from '@/components/fields/card/FieldGeometryTab';
import {
  initialFieldCardTab,
  resolveFieldCardTab,
  type FieldCardTab,
} from '@/components/fields/card/tabs';
import { api, ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import { invalidateMapInit } from '@/lib/map/mapInitCache';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { RenameFieldDialog } from '@/pages/structure/components/RenameFieldDialog';
import type { FarmOption } from '@/pages/structure/components/TransferFieldDialog';
import type { FarmOut } from '@/pages/structure/types';
import { isCapable, CAPABILITY_SOON_HINT } from '@/pages/structure/structureLogic';
import { useCan, PERM } from '@/lib/auth/usePermissions';

// AK-363: тултип для disabled edit-контрола при отсутствии прав.
const NO_PERM_HINT = 'Недостаточно прав для этого действия';

// AK-736: «Тех. операции» переехали из легаси-подэкрана Обзора в свою
// вкладку (`FieldOperationsTab`) — SECONDARY_TITLES теперь только про
// подэкраны, которые ОСТАЛИСЬ внутри Обзора (Рельеф/Осмотры).
const SECONDARY_TITLES: Record<FieldSecondaryView, string> = {
  terrain: 'Рельеф',
  inspections: 'Осмотры',
};

export function FieldDetailPage() {
  const { fieldId } = useParams({ from: '/fields/$fieldId' });
  const navigate = useNavigate();
  // AK-363: правка/архив/восстановление поля — структурные writes
  // (require_structure_write → ресурс `structure`). Нет права → edit-кнопка
  // (Переименовать) дизейблится с тултипом, destructive (Архивировать/
  // Восстановить) — скрывается.
  const canWriteField = useCan(PERM.fieldWrite, 'u');
  // Перенос в другое хозяйство — только tenant-level `structure` (ADR-081 D5).
  const canTransferField = useCan(PERM.fieldTransfer, 'u');
  // Посев = запись в sowings (require_writable_season + ресурс sowings). Гейтим
  // редакторы «Севооборот» на cropWrite; season-lock backend.
  const canWriteCrop = useCan(PERM.cropWrite, 'u');
  // AK-929: содержимое поля (имя, граница, подзоны, рисовая система) — шире
  // структуры: его ведёт и гл. агроном хозяйства (ADR-081 D9). Архив /
  // восстановление / угодья / жизненный цикл остаются на canWriteField.
  const canEditFieldContent = useCan(PERM.fieldContentWrite, 'u');

  // Контролируемое значение вкладки. resolveFieldCardTab гарантирует, что любые
  // легаси-id (occupy/attrs/transfers/map/…) сводятся к валидной v3-вкладке.
  // Стартовая вкладка — из `?tab=` (ссылка «Запланировать →» из попапа поля на
  // /map ведёт сразу на «Посевы»); по умолчанию Обзор.
  const [activeTab, setActiveTab] = useState<FieldCardTab>(initialFieldCardTab);
  const gotoTab = (tab: string) => {
    setSecondaryView(null);
    setActiveTab(resolveFieldCardTab(tab));
  };
  // Легаси-подэкраны (Рельеф / Тех. операции / Осмотры) — временно строки-ссылки
  // на Обзоре, разворачиваются здесь; полноценные виджеты — PR-B.
  const [secondaryView, setSecondaryView] = useState<FieldSecondaryView | null>(
    null,
  );

  const [detail, setDetail] = useState<FieldDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [farms, setFarms] = useState<FarmOption[]>([]);

  const reloadDetail = () => {
    if (!fieldId) return;
    api
      .get<FieldDetail>(`/api/v1/fields/${fieldId}/detail`)
      .then(setDetail)
      .catch((err) => {
        if (err instanceof ApiError) toast.error(err.detail);
        else toast.error('Не удалось загрузить поле');
      });
  };

  useEffect(() => {
    if (!fieldId) return;
    setLoading(true);
    api
      .get<FieldDetail>(`/api/v1/fields/${fieldId}/detail`)
      .then(setDetail)
      .catch((err) => {
        if (err instanceof ApiError) toast.error(err.detail);
        else toast.error('Не удалось загрузить поле');
      })
      .finally(() => setLoading(false));
  }, [fieldId]);

  // Список хозяйств — для селекта приёмника в модалке передачи. Все активные;
  // архивные исключаются в transferTargetFarms.
  useEffect(() => {
    api
      .get<FarmOut[]>('/api/v1/farms')
      .then((rows) =>
        setFarms(rows.map((f) => ({ id: f.id, name: f.name, archived: false }))),
      )
      .catch(() => setFarms([]));
  }, []);

  async function handleArchive() {
    if (!fieldId) return;
    setArchiving(true);
    try {
      await api.delete(`/api/v1/fields/${fieldId}`);
      // Сбрасываем chip-кеш чтобы на /map поле сразу исчезло.
      invalidateMapInit();
      toast.success(`Поле «${detail?.name ?? ''}» архивировано`);
      navigate({ to: '/fields' });
    } catch (err) {
      if (err instanceof ApiError) toast.error(err.detail);
      else toast.error('Не удалось архивировать поле');
    } finally {
      setArchiving(false);
      setArchiveDialogOpen(false);
    }
  }

  // Восстановление поля из архива — POST /fields/{id}/restore (#393).
  // restore_sowings=true (default): поднимаем каскадно-архивированные посевы.
  async function handleRestore() {
    if (!fieldId) return;
    setRestoring(true);
    try {
      await api.post(`/api/v1/fields/${fieldId}/restore`, {
        restore_sowings: true,
      });
      // Поле вернулось на карту — сбрасываем chip-кеш.
      invalidateMapInit();
      toast.success(`Поле «${detail?.name ?? ''}» восстановлено`);
      reloadDetail();
    } catch (err) {
      if (err instanceof ApiError) toast.error(err.detail);
      else toast.error('Не удалось восстановить поле');
    } finally {
      setRestoring(false);
      setRestoreDialogOpen(false);
    }
  }

  // «📍 На карте» — переход на /map с центрированием на поле (flyTo по центроиду
  // + выделение). Требует геометрию (lat/lng); без неё кнопка disabled.
  const hasGeometry = detail?.lat != null && detail?.lng != null;
  function goToMap() {
    if (!fieldId || !detail) return;
    navigate({
      to: '/map',
      search: {
        field: fieldId,
        lng: detail.lng ?? undefined,
        lat: detail.lat ?? undefined,
      },
    });
  }

  if (!fieldId) return null;

  const breadcrumbs: BreadcrumbItem[] | undefined = detail
    ? [
        { label: 'Поля', to: '/fields' },
        ...(detail.cluster_name
          ? [{
              label: detail.cluster_name,
              to: '/fields',
              search: { cluster_id: detail.cluster_id ?? undefined },
            }]
          : []),
        ...(detail.farm_name
          ? [{
              label: detail.farm_name,
              to: '/fields',
              search: { farm_id: detail.farm_id },
            }]
          : []),
        ...(detail.brigade_name
          ? [{
              label: detail.brigade_name,
              to: '/fields',
              search: { brigade_id: detail.brigade_id ?? undefined },
            }]
          : []),
        { label: detail.name },
      ]
    : undefined;

  // Кнопка «На карте» — в шапке всегда (когда поле загружено). Disabled без геометрии.
  const mapButton = detail ? (
    hasGeometry ? (
      <Button variant="outline" size="sm" onClick={goToMap} aria-label="Показать поле на карте">
        <MapPin className="mr-2 h-4 w-4" />
        На карте
      </Button>
    ) : (
      <Tooltip>
        <TooltipTrigger asChild>
          <span>
            <Button variant="outline" size="sm" disabled aria-label="Показать на карте — нет геометрии">
              <MapPin className="mr-2 h-4 w-4" />
              На карте
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>У поля пока нет геометрии</TooltipContent>
      </Tooltip>
    )
  ) : null;

  return (
    <AppShell>
      {/* Вертикальный ритм страницы — Stack (layout-слой DS v1): шаг задаёт
          контейнер, секции своих mt/mb не ставят.
          pb-safe-fab — clearance под плавающий ФАБ «ИИ» + safe-area (AK-374). */}
      <Stack gap={4} className="pb-safe-fab">
        <PageHeader
          title={detail?.name ?? 'Загрузка…'}
          titleSuffix={detail?.number ? `№${detail.number}` : undefined}
          backTo="/fields"
          backLabel="К списку полей"
          breadcrumbs={breadcrumbs}
          actions={
            detail && !detail.is_archived ? (
              <div className="flex flex-wrap gap-2">
                {mapButton}
                {isCapable('renameField') && canEditFieldContent ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setRenameOpen(true)}
                    aria-label="Переименовать поле"
                  >
                    <Pencil className="mr-2 h-4 w-4" />
                    Переименовать
                  </Button>
                ) : (
                  // Нет права (AK-363) или фича «скоро» — disabled + тултип.
                  // Право приоритетнее: если нет права, показываем NO_PERM_HINT.
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled
                          aria-label={
                            canEditFieldContent
                              ? 'Переименовать поле — скоро'
                              : 'Переименовать поле — нет прав'
                          }
                        >
                          <Pencil className="mr-2 h-4 w-4" />
                          Переименовать
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {canEditFieldContent ? CAPABILITY_SOON_HINT : NO_PERM_HINT}
                    </TooltipContent>
                  </Tooltip>
                )}
                {/* Архивировать — destructive-к-структуре: скрываем без права. */}
                {canWriteField && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setArchiveDialogOpen(true)}
                    aria-label="Архивировать поле"
                  >
                    <Archive className="mr-2 h-4 w-4" />
                    Архивировать
                  </Button>
                )}
              </div>
            ) : detail?.is_archived ? (
              <div className="flex flex-wrap gap-2">
                {mapButton}
                {canWriteField && (
                  <Button
                    size="sm"
                    onClick={() => setRestoreDialogOpen(true)}
                    aria-label="Восстановить поле"
                  >
                    <ArchiveRestore className="mr-2 h-4 w-4" />
                    Восстановить
                  </Button>
                )}
              </div>
            ) : undefined
          }
        />

        <Dialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Архивировать поле?</DialogTitle>
              <DialogDescription>
                Поле «{detail?.name ?? ''}» будет скрыто из списков и с карты,
                все его посевы тоже будут архивированы. Действие обратимо —
                восстановить можно в один клик.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setArchiveDialogOpen(false)}
                disabled={archiving}
              >
                Отмена
              </Button>
              <Button
                variant="destructive"
                onClick={handleArchive}
                disabled={archiving}
              >
                {archiving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Архивирую…
                  </>
                ) : (
                  <>
                    <Archive className="mr-2 h-4 w-4" />
                    Архивировать
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Восстановить поле?</DialogTitle>
              <DialogDescription>
                Поле «{detail?.name ?? ''}» снова появится в списках и на карте.
                Посевы, скрытые вместе с полем, тоже вернутся.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setRestoreDialogOpen(false)}
                disabled={restoring}
              >
                Отмена
              </Button>
              <Button
                onClick={handleRestore}
                disabled={restoring}
              >
                {restoring ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Восстанавливаю…
                  </>
                ) : (
                  <>
                    <ArchiveRestore className="mr-2 h-4 w-4" />
                    Восстановить
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {detail && (
          <RenameFieldDialog
            open={renameOpen}
            onOpenChange={setRenameOpen}
            fieldId={fieldId}
            currentName={detail.name}
            fieldPayloadBase={{
              farm_id: detail.farm_id,
              brigade_id: detail.brigade_id ?? null,
              number: detail.number ?? null,
              soil_type: detail.soil_type ?? null,
              mechanical_composition: detail.mechanical_composition ?? null,
              ownership_type: detail.ownership_type ?? null,
              has_irrigation: detail.has_irrigation,
              irrigation_type: detail.irrigation_type ?? null,
              comment: detail.comment ?? null,
            }}
            onRenamed={() => {
              invalidateMapInit();
              reloadDetail();
            }}
          />
        )}

        {loading && !detail ? (
          <div className="space-y-3">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : detail ? (
          <>
            {/* Принадлежность поля (хозяйство / бригада / кластер / орошение /
                сезон) — однородный ряд `secondary`. Раньше здесь было шесть
                палитр в одной строке (purple / orange / cyan / blue / primary +
                красный «В архиве»): цвет ничего не кодировал, но кричал громче
                названия поля. Смысловой цвет остаётся только там, где он
                действительно значение: архив = destructive (design review
                2026-08-18, зона 1 · А5). */}
            <div className="flex flex-wrap gap-2">
              {detail.farm_name && <Badge variant="secondary">{detail.farm_name}</Badge>}
              {detail.brigade_name && (
                <Badge variant="secondary">{detail.brigade_name}</Badge>
              )}
              {detail.cluster_name && (
                <Badge variant="secondary">{detail.cluster_name}</Badge>
              )}
              {detail.has_irrigation && <Badge variant="secondary">Орошение</Badge>}
              {detail.is_archived && <Badge variant="destructive">В архиве</Badge>}
              {detail.current_season && (
                <Badge variant="outline">
                  {detail.current_season.year ?? detail.current_season.code} сезон
                </Badge>
              )}
            </div>

            <Card className="p-0">
              <CardContent className="p-0">
                <Tabs
                  value={activeTab}
                  onValueChange={(v) => gotoTab(v)}
                  className="px-2 md:px-6"
                >
                  {/* Карточка поля v3 (эталон founder 2026-07-11, + AK-736
                      «Тех. операции» 21.08): 7 вкладок. */}
                  <TabsList className="flex-wrap h-auto">
                    <TabsTrigger value="overview">Обзор</TabsTrigger>
                    <TabsTrigger value="rotation">Посевы</TabsTrigger>
                    <TabsTrigger value="history">Севооборот</TabsTrigger>
                    <TabsTrigger value="geometry">Геометрия</TabsTrigger>
                    <TabsTrigger value="journal">Журнал</TabsTrigger>
                    <TabsTrigger value="ops">Тех. операции</TabsTrigger>
                    <TabsTrigger value="cadastre">Кадастры</TabsTrigger>
                    <TabsTrigger value="mgmt">Управление</TabsTrigger>
                  </TabsList>

                  <TabsContent value="overview">
                    {secondaryView ? (
                      <div className="space-y-3 py-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSecondaryView(null)}
                          className="text-muted-foreground"
                        >
                          <ArrowLeft className="mr-2 h-4 w-4" />
                          Назад к обзору · {SECONDARY_TITLES[secondaryView]}
                        </Button>
                        {secondaryView === 'terrain' && (
                          <FieldTerrainTab fieldId={fieldId} />
                        )}
                        {secondaryView === 'inspections' && (
                          <FieldInspectionsTab fieldId={fieldId} />
                        )}
                      </div>
                    ) : (
                      <FieldPassportTab
                        detail={detail}
                        fieldId={fieldId}
                        canWrite={canWriteField}
                        onGoto={gotoTab}
                        onOpenSecondary={setSecondaryView}
                      />
                    )}
                  </TabsContent>

                  <TabsContent value="rotation">
                    <OccupySeasonTab
                      detail={detail}
                      fieldId={fieldId}
                      canWrite={canWriteCrop}
                    />
                  </TabsContent>

                  {/* Ввод истории культур по годам (В-1). Прошлые сезоны —
                      здесь, живой сезон — на «Посевах». */}
                  <TabsContent value="history">
                    <RotationHistoryTab
                      fieldId={fieldId}
                      canWrite={canWriteCrop}
                      isRiceSystem={Boolean(detail.is_rice_system)}
                    />
                  </TabsContent>

                  {/* AK-736 — полный список операций поля своей вкладкой
                      (были только виджет «Обзора» + легаси-подэкран «все →»). */}
                  <TabsContent value="ops">
                    <FieldOperationsTab fieldId={fieldId} />
                  </TabsContent>

                  <TabsContent value="geometry">
                    <FieldGeometryTab
                      fieldId={fieldId}
                      farmId={detail.farm_id}
                      canWrite={canEditFieldContent}
                      onGoto={gotoTab}
                      onGeometryChanged={reloadDetail}
                    />
                  </TabsContent>

                  <TabsContent value="journal">
                    <FieldJournalTab fieldId={fieldId} />
                  </TabsContent>

                  <TabsContent value="cadastre">
                    <Card>
                      <CardContent className="p-6 text-muted-foreground">
                        Кадастровые номера — UI портируется в Phase 5B.
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="mgmt">
                    <FieldManagementTab
                      detail={detail}
                      fieldId={fieldId}
                      canEditContent={canEditFieldContent}
                      canWrite={canWriteField}
                      canTransfer={canTransferField}
                      farms={farms}
                      onRename={() => setRenameOpen(true)}
                      onChanged={reloadDetail}
                    />
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </>
        ) : (
          <Card>
            <CardContent className="p-6 text-muted-foreground">
              Поле не найдено или нет доступа.
            </CardContent>
          </Card>
        )}
      </Stack>
    </AppShell>
  );
}

export default FieldDetailPage;
